import {DataTableProps} from "./DataTable";
import {MouseEvent, MouseEventHandler, useCallback, useMemo, useState} from "react";
import {compareString, CostedEntity, ObjectCostRateModelEntity} from "@ots-frontend/ots-data";


export type UseCostedDataTableResult = {
  rateModels: string[]
  onRateModelSelectOpened: MouseEventHandler;
  onRateModelChange: (value: string | null) => void;
  rateModelSelectOpened: boolean
  setRateModelSelectOpened: (opened: boolean) => void;
  selectedRateModel?: string
  setSelectedRateModel: (value: string | undefined) => void;
}

export type UseCostedDataTableProps<T extends CostedEntity> = Pick<DataTableProps<T>, 'records'>

export function useCostedDataTable<T extends CostedEntity>(props: UseCostedDataTableProps<T>): UseCostedDataTableResult {
  const {records} = props;

  const [_rateModelSelectOpened, _setRateModelSelectOpened] = useState<boolean>(false);
  const [_selectedRateModel, _setSelectedRateModel] = useState<string | undefined>(undefined)

  const _rateModels = useMemo(() => {
    if (records) {
      const rateModelNames = records
        .map(t => t.objectCostRateModels)
        .filter((ocrms): ocrms is ObjectCostRateModelEntity[] => !!ocrms)
        .flat()
        .map(ocrm => ocrm.rateModelName)
        .filter((rm): rm is string => !!rm)

      return [...new Set([...rateModelNames])].sort(compareString)
    } else {
      return []
    }
  }, [JSON.stringify(records)])

  const _onRateModelSelectOpened = useCallback((e: MouseEvent) => {
    e.preventDefault()
    _setRateModelSelectOpened(prev => !prev)
  }, [])

  const _onRateModelChange = useCallback((value: string | null) => {
    _setSelectedRateModel(value ? value : undefined)
    _setRateModelSelectOpened(false)
  }, [])

  return {
    rateModels: _rateModels,
    onRateModelSelectOpened: _onRateModelSelectOpened,
    onRateModelChange: _onRateModelChange,
    rateModelSelectOpened: _rateModelSelectOpened,
    setRateModelSelectOpened: _setRateModelSelectOpened,
    selectedRateModel: _selectedRateModel,
    setSelectedRateModel: _setSelectedRateModel,
  };
}
